import { useState, useEffect, useMemo } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { useAuth } from '@/providers/AuthProvider';

export interface MessageThreadItem {
  id: string;
  content: string;
  senderId: string;
  receiverId: string;
  participants: string[];
  seen: boolean;
  createdAt: Date;
  type?: string;
  mediaUrl?: string;
}

interface UseMessagesResult {
  messages: MessageThreadItem[];
  unreadCount: number;
  unreadByUser: Record<string, number>;
  lastMessageByUser: Record<string, MessageThreadItem>;
  loading: boolean;
  error: string | null;
}

export function useMessages(refreshKey = 0) {
  const [messages, setMessages] = useState<MessageThreadItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setMessages([]);
      setUnreadCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Listen for messages in Firestore
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt || Date.now());

        return {
          id: doc.id,
          content: typeof data.content === 'string' ? data.content : '',
          senderId: typeof data.senderId === 'string' ? data.senderId : '',
          receiverId: typeof data.receiverId === 'string' ? data.receiverId : '',
          participants: Array.isArray(data.participants)
            ? data.participants.filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
            : [],
          seen: Boolean(data.seen),
          createdAt,
          type: typeof data.type === 'string' ? data.type : undefined,
          mediaUrl: typeof data.mediaUrl === 'string' ? data.mediaUrl : undefined,
        } as MessageThreadItem;
      });
      
      setMessages(messagesData);
      
      // Count unread messages where current user is the receiver
      const unread = messagesData.filter((m) => 
        m.receiverId === user.uid && !m.seen
      ).length;
      
      setUnreadCount(unread);
      setLoading(false);
    }, (error) => {
      const errorCode = String((error as any)?.code || '').toLowerCase();
      const isPermissionDenied = errorCode.includes('permission-denied');

      if (!isPermissionDenied) {
        console.error('[useMessages] Error fetching messages:', error);
      }

      setError(isPermissionDenied ? 'Sign in to load messages.' : 'Failed to load messages');
      if (isPermissionDenied) {
        setMessages([]);
        setUnreadCount(0);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid, refreshKey]);

  const unreadByUser = useMemo(() => {
    if (!user) return {};

    return messages.reduce<Record<string, number>>((acc, message) => {
      if (message.receiverId !== user.uid || message.seen) {
        return acc;
      }

      const otherUserId = message.senderId;
      if (!otherUserId) return acc;

      acc[otherUserId] = (acc[otherUserId] || 0) + 1;
      return acc;
    }, {});
  }, [messages, user]);

  const lastMessageByUser = useMemo(() => {
    if (!user) return {};

    return messages.reduce<Record<string, MessageThreadItem>>((acc, message) => {
      const otherUserId = message.senderId === user.uid ? message.receiverId : message.senderId;
      if (!otherUserId) return acc;

      const previous = acc[otherUserId];
      if (!previous || message.createdAt > previous.createdAt) {
        acc[otherUserId] = message;
      }

      return acc;
    }, {});
  }, [messages, user]);

  return {
    messages,
    unreadCount,
    unreadByUser,
    lastMessageByUser,
    loading,
    error,
  } as UseMessagesResult;
}